import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import styles from "./reset-password.module.scss";
import { useRouter } from "next/router";
import AppInput from "@/components/forms/Fields/inputs/AppInput/AppInput";
import AppButton from "@/components/buttons/AppButton/AppButton";
import { useDispatch } from "react-redux";
import { ErrorResponse, SuccessResponse } from "@/types/Response";
import { useState } from "react";
import { addNotification } from "@/redux/reducer/notificationSlice";
import Link from "next/link";
import AuthLayout from "@/layout/AuthLayout/AuthLayout";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { forgetPassword } from "@/utils/auth/forgetPassword";
import { parseCookies, setCookie } from "nookies";
import path from "path";
import fs from "fs";
import { GetServerSideProps } from "next";
import Image from "next/image";
import { useTranslations } from "next-intl";

export interface ForgetPasswordProps {
  translations: Record<string, string>;
}

const ForgetPassword: React.FC<ForgetPasswordProps> = ({
  translations,
}) => {
  const t = useTranslations()
  const router = useRouter();
  const dispatch = useDispatch();
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(translations.emailRequired)
      .email(translations.emailInvalid)
      .matches(
        /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        translations.emailInvalid)
      .max(50, translations.emailMax),
  });

  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const handleSubmit = async (
    values: { email: string },
    actions: FormikHelpers<{ email: string }>
  ) => {
    setLoading(true);
    setDisabled(false);
    try {
      setLoading(true);
      const response = await forgetPassword(values.email);
      if (!response.success) {
       
          const errorResponse: ErrorResponse = await response;
          setLoading(false);

          dispatch(
            addNotification({
              type: "error",
              title: translations.errorTitle,
              subtitle: errorResponse.message || translations.errorSubtitle,
            })
          );
        
        setDisabled(true);
        return;
      }

      const data = await response;

      dispatch(
        addNotification({
          type: "success",
          title: translations.successTitle,
          subtitle: data.message,
        })
      );
      setCookie(null, "next-auth.stored-email", btoa(values.email), {
        path: "/",
        maxAge: 10 * 60, // 10min
      });
      const locale = router.locale;
      router.replace(`/verify-password`, undefined, { locale: locale });

    } catch (error) {
      setLoading(false);

      // console.error("Error resetting password:", error);
    } finally {
      actions.setSubmitting(false);
      // setLoading(false);
    }
  };

  const initialValues = {
    email: "",
  };

  return (
     <AuthLayout children={
 <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange
        validateOnBlur
        onSubmit={handleSubmit}
      >
        {({
          isValid,
          isSubmitting,
          handleSubmit,
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          dirty,
        }) => {
          return (
            <Form  onSubmit={handleSubmit}
                        className="flex flex-col w-full gap-5"
                        onChange={() => setDisabled(false)}>
{/* <Image
                  src="/images/fazaa.png"
                  alt="Fazaa Logo"
                  width={97}
                  height={44}
                  style={{ objectFit: "cover" }}
                /> */}
                <Stack spacing="34px">
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                fontSize: {
                        xs: "20px",
                        sm: "40px",
                      },
                      lineHeight: {
                        xs: "28px",
                        sm: "40px",
                      },
                      color: "#1e2f56",
                    }}
                  >
                                       {translations.forgotPasswordTitle}

                  </Typography>
                  <Typography
                    sx={{
             fontSize: {
                        xs: "16px",
                        sm: "18px",
                      },
                      lineHeight: {
                        xs: "20px",
                        sm: "27px",
                      },
                      color: "#1e2f56",
                    }}
                  >
                    {translations.forgotPasswordSubtitle}
                  </Typography>
                </Stack>
                <Stack spacing="38px">
                  <Box>
                    <AppInput
                      label={translations.Email}
                      name="email"
                      value={values.email}
                      onChange={handleChange("email")}
                      onBlur={handleBlur("email")}
                  
                      error={touched.email && errors.email}
                      labelClass="!font-medium !text-base leading-5 mb-2 !text-primaryText"
                      placeholder={t("email_Placeholder")}
                      className="  bg-gray-50 !h-[55px] !rounded-[12px] border !border-gray-300 hover:border-gray-300 focus-within:border-gray-300 !text-base !text-gray-600 px-3  outline-none"
                    />
                   
                  </Box>
                
                 
                  <AppButton
                    className="h-[56px] bg-primary !rounded-lg font-bold text-[20px] leading-6 hover:bg-primaryHover text-white px-4"
                    variant="primary"
                    label={translations.continueButton}
                    type="submit"
                    loading={loading}
                    disabled={!isValid || !dirty || loading  }
                  />

                  <Link
                        href="/signin"
                        className="  text-[13px]   flex text-center justify-center   text-[#767676]"
                      >
                        {translations.returnToSignIn}
                      </Link>
                </Stack>
       
            </Form>
          );
        }}
      </Formik>
     } />

     
  

  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const { locale } = context;

    const commonFilePath = path.join(
      process.cwd(),
      "src",
      "locales",
      locale ?? "en",
      "auth.json"
    );
    const commonData = fs.readFileSync(commonFilePath, "utf-8");
    const translations = JSON.parse(commonData);
    const cookies = parseCookies(context);

    if (cookies['next-auth.session-token']) {
      return {
        redirect: {
          destination: '/home',
          permanent: false,
          locale
        },
      };
    }
    return {
      props: {
        translations,
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
};

export default ForgetPassword;
